/* eslint-disable unicode-bom */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Link from '@mui/icons-material/Link';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { apiUrl, fetchJson } from '../../apiBase';
import Skeleton from '@mui/material/Skeleton';

export default function ClassesManager({ schoolId, onError, onSuccess, readOnly = false }) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState('');
  const [repId, setRepId] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [teacherIds, setTeacherIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [registrationClass, setRegistrationClass] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const resetForm = () => { setName(''); setCapacity(''); setRepId(''); setTeacherIds([]); };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}${readOnly ? '' : '/manage'}`));
      if (!res.ok) throw new Error('load');
      const items = await res.json();
      setClasses(items || []);
      if (!readOnly) {
        const teachersRes = await fetchJson(apiUrl(`/admin/teachers/${encodeURIComponent(schoolId)}`));
        if (!teachersRes.ok) throw new Error('load');
        const teacherItems = await teachersRes.json();
        setTeachers(Array.isArray(teacherItems) ? teacherItems : (teacherItems.teachers || []));
      }
    } catch (e) {
      onError && onError('Şubeler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [onError, readOnly, schoolId]);

  useEffect(() => { if (schoolId) refresh(); }, [schoolId, refresh]);

  const sortedClasses = useMemo(() => {
    return [...(classes || [])].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'tr-TR'));
  }, [classes]);

  const filteredClasses = useMemo(() => {
    const q = (query || '').toLocaleLowerCase('tr-TR');
    if (!q) return sortedClasses;
    return sortedClasses.filter((c) => String(c?.name || '').toLocaleLowerCase('tr-TR').includes(q));
  }, [sortedClasses, query]);

  const openNew = () => { setEditing(null); resetForm(); setDlgOpen(true); };
  const openEdit = (cls) => { setEditing(cls); setName(cls.name || ''); setCapacity(cls.capacity || ''); setRepId(String(cls.class_representative_teacher_id || '')); setTeacherIds((cls.teacher_ids || []).map(String)); setDlgOpen(true); };
  const teacherLabel = (teacherId) => {
    const teacher = teachers.find((t) => String(t.id) === String(teacherId));
    return teacher ? `${teacher.name || ''} ${teacher.surname || ''}`.trim() : String(teacherId);
  };
  const classUrl = (cls) => apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}/${cls.id ? `by-id/${encodeURIComponent(cls.id)}` : encodeURIComponent(cls.name)}`);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (!name.trim()) { onError && onError('Şube adı zorunlu'); return; }
      const body = { name: name.trim(), capacity: capacity ? Number(capacity) : undefined, class_representative_teacher_id: repId ? Number(repId) : null, teacher_ids: [...new Set([...teacherIds, ...(repId ? [repId] : [])])].map(Number) };
      let res;
      if (editing && editing.name) {
        // update; allow rename using same field
        res = await fetchJson(classUrl(editing), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      } else {
        res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      }
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(typeof error.detail === 'string' ? error.detail : 'Şube kaydedilemedi');
      }
      onSuccess && onSuccess('Şube kaydedildi');
      setDlgOpen(false); setEditing(null);
      refresh();
    } catch (e) { onError && onError(e.message || 'Şube kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const remove = async (cls) => {
    if (!window.confirm('Şube silinsin mi?')) return;
    try {
      const res = await fetchJson(classUrl(cls), { method: 'DELETE' });
      if (!res.ok) throw new Error('delete');
      onSuccess && onSuccess('Şube silindi');
      refresh();
    } catch (e) { onError && onError('Şube silinemedi'); }
  };

  const copyRegistrationLink = async () => {
    try {
      await navigator.clipboard.writeText(registrationClass.registration_link);
      onSuccess && onSuccess('Veli kayıt bağlantısı kopyalandı');
    } catch (e) {
      onError && onError('Bağlantı kopyalanamadı. Bağlantıyı seçerek kopyalayabilirsiniz.');
    }
  };

  const regenerateRegistrationLink = async () => {
    if (regenerating || !window.confirm('Yeni bağlantı oluşturulsun mu? Eski bağlantı artık kayıt için kullanılamayacak.')) return;
    setRegenerating(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}/by-id/${encodeURIComponent(registrationClass.id)}/registration/regenerate`), { method: 'POST' });
      if (!res.ok) throw new Error('Bağlantı yenilenemedi');
      const updated = await res.json();
      setRegistrationClass(updated);
      setClasses((items) => items.map((item) => String(item.id) === String(updated.id) ? updated : item));
      onSuccess && onSuccess('Yeni veli kayıt bağlantısı oluşturuldu');
    } catch (e) {
      onError && onError(e.message || 'Bağlantı yenilenemedi');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{readOnly ? 'Atandığım Şubeler' : 'Şubeler'}</Typography>
              {!readOnly && <Button startIcon={<Add />} size="small" variant="contained" onClick={openNew} disabled={loading}>Yeni Şube</Button>}
            </Box>
            <Box sx={{ mb: 1 }}>
              <TextField
                label="Şube Ara"
                size="small"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Yazdıkça filtrelenir"
                fullWidth
              />
            </Box>
            {loading ? (
              <List dense>
                {Array.from({ length: 6 }).map((_, i) => (
                  <ListItem key={`s-${i}`}>
                    <ListItemText primary={<Skeleton variant="text" width="40%" />} secondary={<Skeleton variant="text" width="30%" />} />
                  </ListItem>
                ))}
              </List>
            ) : classes.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body1" sx={{ mb: !readOnly ? 2 : 0 }}>{readOnly ? 'Size atanmış şube bulunmuyor' : 'Henüz şube eklenmedi'}</Typography>
                {!readOnly && <Button startIcon={<Add />} variant="contained" size="small" onClick={openNew}>+ Yeni Şube</Button>}
              </Box>
            ) : filteredClasses.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">Arama kriterlerinize uygun sonuç bulunamadı</Typography>
              </Box>
            ) : (
              <List dense>
                {filteredClasses.map((c) => (
                  <ListItem key={c.id || c.name} sx={{ gap: 1, flexWrap: 'wrap' }}>
                    <ListItemText primary={<Typography variant="body1" sx={{ fontWeight: 500 }}>{c.name}</Typography>} secondary={readOnly ? `Kapasite: ${c.capacity || '-'}` : `Kapasite: ${c.capacity || '-'} | Rehber Öğretmen: ${c.class_representative_teacher_id ? teacherLabel(c.class_representative_teacher_id) : '-'} | Öğretmenler: ${(c.teacher_ids || []).map(teacherLabel).join(', ') || '-'}`} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {c.registration_link && <Button size="small" startIcon={<Link />} aria-label={`${c.name} veli kayıt bağlantısı`} onClick={() => setRegistrationClass(c)}>Veli kayıt linki</Button>}
                    {!readOnly && <>
                      <IconButton edge="end" aria-label={`${c.name} şubesini düzenle`} onClick={() => openEdit(c)}><Edit /></IconButton>
                      <IconButton edge="end" aria-label={`${c.name} şubesini sil`} onClick={() => remove(c)}><Delete /></IconButton>
                    </>}
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={Boolean(registrationClass)} onClose={() => { if (!regenerating) setRegistrationClass(null); }} fullWidth maxWidth="sm">
        <DialogTitle>{registrationClass?.name} veli kayıt bağlantısı</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>Bu bağlantıyı sınıfınızın WhatsApp grubunda paylaşın. Veliler adlarını ve çocuklarının adlarını WhatsApp üzerinden paylaşarak kayıtlarını tamamlayabilir.</Typography>
          <TextField label="Veli kayıt bağlantısı" value={registrationClass?.registration_link || ''} fullWidth InputProps={{ readOnly: true }} onFocus={(event) => event.target.select()} />
          {!readOnly && <Button sx={{ mt: 2 }} color="warning" onClick={regenerateRegistrationLink} disabled={regenerating}>{regenerating ? 'Yenileniyor…' : 'Bağlantıyı yenile'}</Button>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegistrationClass(null)} disabled={regenerating}>Kapat</Button>
          <Button variant="contained" startIcon={<ContentCopy />} onClick={copyRegistrationLink} disabled={regenerating || !registrationClass?.registration_link}>Bağlantıyı kopyala</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlgOpen} onClose={() => { if (!saving) setDlgOpen(false); }} fullWidth maxWidth="sm">
        <DialogTitle>Şube Formu</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
            <TextField label="Şube Adı" inputProps={{ 'aria-label': 'Şube Adı' }} required value={name} onChange={(e) => setName(e.target.value)} size="small" />
            <TextField label="Kapasite" inputProps={{ 'aria-label': 'Kapasite' }} type="number" value={capacity} onChange={(e) => setCapacity(e.target.value)} size="small" />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={teachers.map((t) => String(t.id))}
              value={repId || null}
              getOptionLabel={teacherLabel}
              onChange={(_, value) => {
                setRepId(value || '');
                if (value) setTeacherIds((ids) => [...new Set([...ids, value])]);
              }}
              renderInput={(params) => <TextField {...params} label="Rehber Öğretmen" />}
              noOptionsText="Öğretmen bulunamadı"
              size="small"
              disabled={saving}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              multiple
              options={teachers.map((t) => String(t.id))}
              value={teacherIds}
              getOptionLabel={teacherLabel}
              onChange={(_, values) => setTeacherIds([...new Set([...values, ...(repId ? [repId] : [])])])}
              renderInput={(params) => <TextField {...params} label="Atanmış Öğretmenler" helperText="Rehber öğretmen atamaya dahildir. Çıkarmak için önce rehber öğretmen seçimini kaldırın." />}
              noOptionsText="Öğretmen bulunamadı"
              size="small"
              disabled={saving}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)} disabled={saving}>İptal</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
